/**
 * Delivery rates, and the thresholds that make them mean something.
 *
 * The screen this serves used to show raw counts: "Bounced 102". That is not a
 * number anybody can act on — 102 out of four million is spotless and 102 out
 * of four hundred will get the account suspended. The denominator is the whole
 * message.
 *
 * THE THRESHOLDS ARE A MIRROR, AND MIRRORS DRIFT.
 *
 * They are duplicated from two places that already agree with each other:
 *
 *   internal/deliverability/breaker.go   BounceRateThreshold    = 0.04
 *                                        ComplaintRateThreshold = 0.001
 *   deploy/prometheus-rules.yml          bounce    > 0.02   (warning)
 *                                        complaint > 0.001  (critical)
 *
 * That pairing is deliberate on the server side: the bounce alert warns at half
 * the rate the breaker trips at, and the complaint alert fires at the breaker's
 * own rate, so an operator and the breaker never tell two different stories. This file is a third copy, and the
 * only honest thing to say about it is that a browser cannot read a Go constant.
 * If you change one, change all three; a dashboard that says "healthy" while the
 * breaker is pausing sends is worse than a dashboard that says nothing.
 */

/** How a rate compares to its thresholds. */
export type Health = "ok" | "warn" | "critical";

export interface Threshold {
  /** Above this, something is wrong but sending continues. */
  warn: number;
  /**
   * Above this, the circuit breaker pauses sending for the organization.
   * Undefined for rates nothing enforces.
   */
  critical?: number;
}

export const THRESHOLDS = {
  /** Warns at half the rate the breaker trips at, matching the alert rule. */
  bounce: { warn: 0.02, critical: 0.04 },
  /**
   * The breaker and the alert use the same number here, unlike bounces. A
   * complaint rate has no comfortable band: 0.1% is already the point at which
   * mailbox providers start filtering, so there is nothing to warn about below
   * the level that matters.
   */
  complaint: { warn: 0.001, critical: 0.001 },
  /**
   * Enforced by nothing. Included because a climbing unsubscribe rate is the
   * earliest signal that a list is being over-mailed, and it shows up long
   * before the complaint rate does. 0.5% is a convention rather than a rule,
   * and the screen says so rather than implying the breaker cares.
   */
  unsubscribe: { warn: 0.005 },
} satisfies Record<string, Threshold>;

/** Where a rate sits against its thresholds. */
export function healthOf(rate: number, threshold: Threshold): Health {
  if (threshold.critical !== undefined && rate >= threshold.critical) return "critical";
  if (rate >= threshold.warn) return "warn";
  return "ok";
}

/**
 * A rate, or null when there is no denominator.
 *
 * Null rather than zero, deliberately. Zero out of zero is not a 0% bounce
 * rate — it is the absence of one, and rendering it as 0% claims a clean record
 * for a campaign that never sent anything.
 */
export function rate(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null;
}

/**
 * Formats a rate for display.
 *
 * Precision scales with magnitude because these span three orders. A complaint
 * rate rounded to a whole percent is 0% right up until it is 1%, which is ten
 * times the level that suspends an account — so the number that matters most
 * would be the one displayed least precisely.
 */
export function formatRate(value: number | null): string {
  if (value === null) return "—";
  if (value === 0) return "0%";
  if (value < 0.001) return `${(value * 100).toFixed(3)}%`;
  if (value < 0.01) return `${(value * 100).toFixed(2)}%`;
  if (value < 0.1) return `${(value * 100).toFixed(1)}%`;
  return `${Math.round(value * 100)}%`;
}

export interface Totals {
  sent: number;
  delivered: number;
  bounced: number;
  soft_bounced?: number;
  complained: number;
  failed: number;
  unsubscribed: number;
  clicked: number;
  opens: { total: number; mpp: number; excluding_mpp: number };
  /** Messages, once each; a click counts as an open. What rates are made of. */
  unique: { opened: number; opened_excluding_mpp: number; clicked: number };
}

export interface Rates {
  delivered: number | null;
  bounced: number | null;
  complained: number | null;
  unsubscribed: number | null;
  /** Messages opened, of delivered. A bounced message cannot be opened. */
  opened: number | null;
  openedExcludingMPP: number | null;
  /** Messages clicked, of delivered, so it compares with the open rate. */
  clicked: number | null;
  /** Messages clicked, of messages opened: who opened and then acted. */
  clickThrough: number | null;
}

/**
 * Derives every rate the screen shows.
 *
 * The denominators are the argument. Delivery outcomes divide by `sent` because
 * that is what the provider was asked to do; engagement divides by `delivered`
 * because a message that never arrived cannot be opened, and dividing opens by
 * sent understates every campaign in proportion to how badly it bounced —
 * which is precisely backwards.
 *
 * Engagement counts messages, not events. These were events until 22
 * September: one reader clicking five links was five clicks, and a funnel of
 * events showed more opens than deliveries as soon as anybody opened twice.
 */
export function ratesOf(t: Totals): Rates {
  return {
    delivered: rate(t.delivered, t.sent),
    bounced: rate(t.bounced, t.sent),
    complained: rate(t.complained, t.sent),
    unsubscribed: rate(t.unsubscribed, t.sent),
    opened: rate(t.unique.opened, t.delivered),
    openedExcludingMPP: rate(t.unique.opened_excluding_mpp, t.delivered),
    clicked: rate(t.unique.clicked, t.delivered),
    clickThrough: rate(t.unique.clicked, t.unique.opened),
  };
}

export interface Finding {
  health: Health;
  label: string;
  detail: string;
}

/**
 * What is wrong, worst first — or nothing, which is also an answer.
 *
 * This is the top of the screen because it is the only part somebody has to
 * read. Everything below it is for when the answer is "yes, something", and the
 * reader wants to know what.
 *
 * `volumeFloor` exists for the same reason the Prometheus rules carry one: one
 * bounce out of three is a 33% bounce rate and means nothing at all. Without a
 * floor the loudest warning on a quiet deployment is always noise, and a
 * dashboard that cries wolf on a test send is one nobody reads on the day it
 * matters.
 */
export function findingsFor(t: Totals, volumeFloor = 50): Finding[] {
  const findings: Finding[] = [];
  if (t.sent < volumeFloor) return findings;

  const r = ratesOf(t);

  if (r.bounced !== null) {
    const health = healthOf(r.bounced, THRESHOLDS.bounce);
    if (health !== "ok") {
      findings.push({
        health,
        label: `Bounce rate ${formatRate(r.bounced)}`,
        detail:
          health === "critical"
            ? `At or above ${formatRate(THRESHOLDS.bounce.critical!)}, sending is paused automatically. Mailbox providers read a high bounce rate as a sign the list was not collected with consent.`
            : `Above ${formatRate(THRESHOLDS.bounce.warn)}. Sending pauses automatically at ${formatRate(THRESHOLDS.bounce.critical!)}.`,
      });
    }
  }

  if (r.complained !== null) {
    const health = healthOf(r.complained, THRESHOLDS.complaint);
    if (health !== "ok") {
      findings.push({
        health,
        label: `Complaint rate ${formatRate(r.complained)}`,
        detail: `At or above ${formatRate(THRESHOLDS.complaint.warn)}, sending is paused automatically. A complaint is somebody pressing "this is spam", and providers weigh it far more heavily than a bounce.`,
      });
    }
  }

  if (r.unsubscribed !== null) {
    const health = healthOf(r.unsubscribed, THRESHOLDS.unsubscribe);
    if (health !== "ok") {
      findings.push({
        health: "warn",
        label: `Unsubscribe rate ${formatRate(r.unsubscribed)}`,
        detail:
          "Nothing enforces this one, but it is the earliest signal that a list is being mailed too often — it climbs well before the complaint rate does.",
      });
    }
  }

  // Critical first, then warn. Within a level the order is the order they were
  // checked, which puts bounces above complaints — deliberate, since a bounce
  // rate is the one somebody can fix today by cleaning the list.
  return findings.sort((a, b) => rank(b.health) - rank(a.health));
}

function rank(health: Health): number {
  return health === "critical" ? 2 : health === "warn" ? 1 : 0;
}
